import {
  ArrowPathIcon,
  ArrowTopRightOnSquareIcon,
  ChevronDownIcon,
  PlusIcon,
} from '@heroicons/react/24/outline';
import { ExclamationTriangleIcon } from '@heroicons/react/24/solid';
import React, {
  forwardRef,
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useSearchParams } from 'react-router';
import { toast } from 'sonner';

import { useTheme } from '@/app/ThemeContext';
import {
  Container,
  DashedTag,
  Dropdown,
  DropdownOption,
  TagSeverity,
} from '@/components/chariot-ui';
import { Tabs } from '@/components/chariot-ui';
import { NoInformation as NoInformationIcon } from '@/components/chariot-ui';
import { Entitled } from '@/components/Entitled';
import { createVulnerabilityExportConfig } from '@/components/export/configs/vulnerability.export.config';
import { UniversalExportModal } from '@/components/export/universal';
import { InputText } from '@/components/form/InputText';
import { RisksIcon } from '@/components/icons';
import { Loader } from '@/components/Loader';
import { MarkdownEditor } from '@/components/markdown/MarkdownEditor';
import { MarkdownPreview } from '@/components/markdown/MarkdownPreview';
import { Modal } from '@/components/Modal';
import { NoteModal } from '@/components/modals/NoteModal/NoteModal';
import { PushToPlexTracModal } from '@/components/reporting/PushToPlexTracModal';
import { Table } from '@/components/table/Table';
import TabsKeyboard from '@/components/Tabs';
import { Timeline } from '@/components/Timeline';
import { Button } from '@/components/ui/button';
import { generateSeverityOptions } from '@/features/integrations/utils/severityOptions';
import {
  fetchTicket,
  useAssociateTicket,
  useCreateTicket,
  useDisconnectTicket,
} from '@/hooks/ticket';
import { useUserSettings } from '@/hooks/useAccounts';
import { useQueryAssets } from '@/hooks/useAssets';
import { useAxios } from '@/hooks/useAxios';
import { useEntitlements } from '@/hooks/useEntitlements';
import { useEvidenceFiles } from '@/hooks/useEvidenceFiles';
import { useGetFile, useUploadFile } from '@/hooks/useFiles';
import { useGenericSearch } from '@/hooks/useGenericSearch';
import { AllIntegrations } from '@/hooks/useIntegration';
import { useIntegrationSettings } from '@/hooks/useIntegrationSettings';
import type { PlextracDefinition } from '@/hooks/usePlextrac';
import { useSyncPlextrac } from '@/hooks/usePlextrac';
import { getQueryKey } from '@/hooks/useQueryKeys';
import { useRiskActions } from '@/hooks/useRiskActions';
import {
  useRefreshVisited,
  useReportRisk,
  useUpdateRisk,
} from '@/hooks/useRisks';
import {
  getTicketingIntegration,
  useTicketingAccounts,
} from '@/hooks/useTicketingIntegrations';
import { useVulnerabilityStatusMode } from '@/hooks/useVulnerabilityStatusMode';
import { queryClient } from '@/queryclient';
import { AddTag, SimpleTags } from '@/sections/AddTag';
import { InsufficientPermissionsTooltip } from '@/sections/Integrations';
import { NotesTab } from '@/sections/NotesTab';
import { POESelector } from '@/sections/POESelector';
import SetupModal from '@/sections/SetupModal';
import {
  CVSSComponent,
  EPSSComponent,
  ExploitsTimelineComponent,
  MitreAttacksComponent,
  MitreWeaknessesComponent,
  ThreatActorsComponent,
} from '@/sections/threats/drawer';
import type { EnrichedVulnerability } from '@/sections/threats/types';
import { VulnerabilityActions } from '@/sections/vulnerabilities/components/actions';
import { CorrelationGraph } from '@/sections/vulnerabilities/components/CorrelationGraph';
import { JiraTicketActionsMenu } from '@/sections/vulnerabilities/components/JiraTicketActionsMenu';
import { AssetsTab } from '@/sections/vulnerabilities/components/tabs/AssetsTab';
import {
  useVulnerabilityDrawerData,
  useVulnerabilityDrawerTab,
} from '@/sections/vulnerabilities/hooks/drawer';
import {
  EmbeddedTicket,
  getTicketKey,
  useOptimisticTickets,
} from '@/sections/vulnerabilities/hooks/useOptimisticTickets';
// Lazy-load to avoid pulling in heavy dependencies (jszip, xlsx, papaparse) at startup
const ImportVulnerabilitiesModal = lazy(() =>
  import(
    '@/sections/vulnerabilities/components/modals/ImportVulnerabilitiesModal'
  ).then(module => ({ default: module.ImportVulnerabilitiesModal }))
);
import { VulnerabilityRemoveTag } from '@/sections/vulnerabilities/components/modals/RemoveTagModal';
import {
  MLPrediction,
  VulnerabilityDrawerHeader,
} from '@/sections/vulnerabilities/components/VulnerabilityDrawerHeader';
import { useAuth } from '@/state/auth';
import { useGlobalState } from '@/state/global.state';
import { useOsintContext } from '@/state/OsintContext';
import {
  Account,
  JiraTemplate,
  ResourceType,
  Risk,
  RiskSeverity,
  RiskStatus,
  RiskStatusLabel,
  SeverityDef,
} from '@/types';
import { cn } from '@/utils/classname';
import { mToMs } from '@/utils/date.util';
import { getHistoryDiff } from '@/utils/getHistoryDiff.util';
import { getRiskStatusLabel } from '@/utils/riskStatus.util';
import { getPastTense } from '@/utils/text.util';

const relatedTicketsWidth = 284;

// VulnerabilityDetailsProps - No longer needed since component fetches its own data
// Kept for backward compatibility in case other components use it
interface VulnerabilityDetailsProps {
  risk?: Risk | null; // Made optional - component fetches data internally via key
}

const severityFilters = [
  {
    id: 'C',
    label: SeverityDef[RiskSeverity.Critical],
    value: 'C',
    severity: 'critical' as TagSeverity,
  },
  {
    id: 'H',
    label: SeverityDef[RiskSeverity.High],
    value: 'H',
    severity: 'high' as TagSeverity,
  },
  {
    id: 'M',
    label: SeverityDef[RiskSeverity.Medium],
    value: 'M',
    severity: 'medium' as TagSeverity,
  },
  {
    id: 'L',
    label: SeverityDef[RiskSeverity.Low],
    value: 'L',
    severity: 'low' as TagSeverity,
  },
  {
    id: 'I',
    label: SeverityDef[RiskSeverity.Info],
    value: 'I',
    severity: 'info' as TagSeverity,
  },
  {
    id: 'E',
    label: SeverityDef[RiskSeverity.Exposure],
    value: RiskSeverity.Exposure,
    severity: 'exposure' as TagSeverity,
  },
];

const severityMap: Record<RiskSeverity, TagSeverity> = {
  C: 'critical',
  H: 'high',
  M: 'medium',
  L: 'low',
  I: 'info',
  E: 'exposure',
};

// eslint-disable-next-line react-refresh/only-export-components
export const getRiskTagSeverity = (status: string): TagSeverity => {
  const { severity } = getRiskStatusLabel(status);

  return severityMap[severity ?? RiskSeverity.Info];
};

// eslint-disable-next-line react-refresh/only-export-components
export const getRiskStatus = (status: string): string => {
  const { status: statusLabel } = getRiskStatusLabel(status);

  return getPastTense(RiskStatusLabel[statusLabel]);
};

const sharedTabOptions = [
  {
    label: 'Overview',
    value: 'overview',
  },
  {
    label: 'Assets Impacted',
    value: 'assets',
  },
  {
    label: 'Evidence',
    value: 'evidence',
  },
];

const tabOptions = [
  ...sharedTabOptions,
  {
    label: 'History',
    value: 'history',
  },
  {
    label: 'Notes',
    value: 'notes',
  },
];

// Helper function to parse ML prediction from history
function getMLPredictionFromHistory(
  risk: Risk | null
): MLPrediction | undefined {
  // Add null check to prevent crashes when risk is not loaded yet
  if (!risk || !risk.history) return undefined;

  // Look through history in reverse to get latest ML prediction
  const mlHistory = [...(risk.history || [])]
    .reverse()
    .find(
      h =>
        h.comment?.startsWith('ML Model Prediction:') &&
        typeof h.logit === 'number'
    );

  if (!mlHistory) return undefined;

  return {
    recommendation: mlHistory.comment.split(': ')[1], // Extract O or D
    logit: mlHistory.logit!,
    model: mlHistory.Base!,
    timestamp: mlHistory.updated,
  };
}

/**
 * VulnerabilityDrawerComponent
 *
 * KEY-BASED PATTERN: Uses vulnerabilityDrawerKey from URL params as the single
 * source of truth. The drawer fetches its own data internally via
 * useVulnerabilityDrawerData(key), eliminating race conditions from dual state sources.
 */
export const VulnerabilityDrawerComponent: React.FC<
  VulnerabilityDetailsProps
> = () => {
  // KEY-BASED PATTERN: Read key from URL params via drawerState
  const {
    drawerState: { vulnerabilityDrawerKey },
    keyboardListeners,
  } = useGlobalState();

  // OSINT partition is handled automatically via OsintContext — when the drawer
  // is wrapped in OsintProvider (via MaybeOsint in AppDrawers), all hooks
  // (useMy, useAxios, etc.) automatically use the OSINT partition.
  const { isOsint } = useOsintContext();
  const { canWriteAssets } = useEntitlements();

  // LOCAL STATE PATTERN: Tab state managed locally to prevent race conditions
  // when arrow key navigation rapidly updates URL params
  const [currentTab, setCurrentTab] = useVulnerabilityDrawerTab();

  // Fetch data internally using the key (single source of truth)
  const {
    vulnerability: risk,
    vulnerabilityId,
    vulnerabilityTitle,
    isLoading,
  } = useVulnerabilityDrawerData(vulnerabilityDrawerKey || '');

  const { isPraetorianUser } = useAuth();
  const axios = useAxios();
  const { mode } = useVulnerabilityStatusMode();
  const exportConfig = useMemo(
    () => createVulnerabilityExportConfig(mode),
    [mode]
  );
  const [isTagModalOpen, setIsTagModalOpen] = useState(false);
  const [isRemoveTagModalOpen, setIsRemoveTagModalOpen] = useState(false);
  const [isPlexTracModalOpen, setIsPlexTracModalOpen] = useState(false);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const [isEditingAuxiliaryInfo, setIsEditingAuxiliaryInfo] = useState(false);
  const [auxiliaryInfoValue, setAuxiliaryInfoValue] = useState('');

  const {
    existingWriteup,
    hasExistingWriteup,
    mutate: syncWithPlextrac,
  } = useSyncPlextrac(risk?.name || '', risk?.key || '');

  const {
    data: definitionsFile,
    isFetching: isDefinitionsFileFetching,
    refetch: refetchDefinitionsFile,
  } = useGetFile(
    {
      name: `definitions/${risk?.name}`,
    },
    { enabled: !!risk }
  );

  const definitionsFileValue =
    typeof definitionsFile === 'string'
      ? definitionsFile
      : JSON.stringify(definitionsFile);

  // Per-instance overview file (definitions/{risk.key})
  const { data: instanceOverviewFile } = useGetFile(
    {
      name: `definitions/${risk?.key}`,
    },
    { enabled: !!risk }
  );

  const instanceOverviewValue =
    typeof instanceOverviewFile === 'string'
      ? instanceOverviewFile
      : instanceOverviewFile
        ? JSON.stringify(instanceOverviewFile)
        : '';

  // Evidence file hooks for auxiliary information
  const { data: evidenceFile } = useGetFile(
    {
      name: `evidence/${risk?.key}`,
    },
    {
      enabled: !!risk,
    }
  );

  const { mutateAsync: updateFile, status: updateFileStatus } = useUploadFile();

  const evidenceFileValue =
    typeof evidenceFile === 'string'
      ? evidenceFile
      : evidenceFile
        ? JSON.stringify(evidenceFile)
        : '';

  // Set auxiliary info value when modal opens
  React.useEffect(() => {
    if (isEditingAuxiliaryInfo) {
      setAuxiliaryInfoValue(evidenceFileValue);
    }
  }, [isEditingAuxiliaryInfo, evidenceFileValue]);

  // Fixed: Add null check before calling getMLPredictionFromHistory
  // This prevents the crash when opening vulnerability from share link
  const mlPrediction = useMemo(() => {
    if (!risk) return undefined;
    return getMLPredictionFromHistory(risk);
  }, [risk]);

  const selectedTab =
    tabOptions.find(tab => tab.value === currentTab) || tabOptions[0];

  const { mutateAsync: updateRisk } = useUpdateRisk();
  const { mutate: refreshVisited, status: refreshVisitedStatus } =
    useRefreshVisited();

  async function handleUpdateRisk(risk: Risk) {
    await updateRisk(risk);
  }

  const [isNoteModalOpen, setIsNoteModalOpen] = useState(false);
  const [isTicketModalOpen, setIsTicketModalOpen] = useState(false);

  // ✅ FIXED: Move all hooks BEFORE the early return to comply with Rules of Hooks
  // All hooks must be called unconditionally before any early returns
  const attributesFilter = risk ? `source:#risk#${risk.dns}#${risk.name}` : '';
  const { data: attributesGenericSearch, refetch } = useGenericSearch(
    {
      query: attributesFilter,
      exact: true,
      label: ResourceType.Attribute,
    },
    {
      // Disable ticket attribute queries in OSINT mode — OSINT risks don't have tickets
      enabled: !!risk && !isOsint,
    }
  );

  const assetParentFilters = useMemo(
    () => ({ riskKey: risk?.key || '' }),
    [risk?.key]
  );

  const { data: impactedAssets } = useQueryAssets({
    parentFilters: assetParentFilters,
    excludeAccountSources: true,
    enabled: !!risk,
  });

  const { data: accessGeneric } = useGenericSearch({
    query: '#access',
  });
  const accesss = accessGeneric?.accesss || [];

  const { data: evidenceFiles } = useEvidenceFiles(risk?.key || '');

  // Server tickets from API (source of truth)
  // Supports any ticketing integration (Jira, Azure DevOps, etc.)
  // Tickets are now embedded on the Risk node itself
  const serverTicketsRaw = risk?.tickets || [];
  const serverTicketsFingerprint = serverTicketsRaw
    .map(t => `${t.provider}:${t.id}:${t.status}`)
    .join(',');
  const serverTickets = useMemo(
    () => serverTicketsRaw,
    // Stabilize: only change when ticket identities actually change
    [serverTicketsFingerprint]
  );

  // Optimistic tickets for instant UI feedback when status changes to "Opened"
  // Shows "Creating..." placeholder immediately, replaced by real ticket on refetch
  const {
    tickets,
    addOptimisticTicket,
    hasOptimisticTicket,
    removeOptimisticTicket,
    restoreOptimisticTicket,
  } = useOptimisticTickets(serverTickets);

  const { accounts: ticketingAccounts } = useTicketingAccounts();

  // Refs for effect body — fresh values without being dependencies
  const serverTicketsRef = useRef(serverTickets);
  const ticketingAccountsRef = useRef(ticketingAccounts);

  useEffect(() => {
    serverTicketsRef.current = serverTickets;
    ticketingAccountsRef.current = ticketingAccounts;
  });

  const [refreshedMetadata, setRefreshedMetadata] = useState<
    Record<string, { status?: string; assignee?: string; resolution?: string }>
  >({});

  // Fetch fresh ticket data when the drawer opens or after a mutation changes the risk
  useEffect(() => {
    const currentTickets = serverTicketsRef.current;
    const currentAccounts = ticketingAccountsRef.current;

    // Skip if no tickets
    if (currentTickets.length === 0) return;

    const controller = new AbortController();

    const fetchTicketData = async () => {
      const newMetadata: Record<
        string,
        { status?: string; assignee?: string; resolution?: string }
      > = {};

      for (const ticket of currentTickets) {
        try {
          const ticketKey = `${ticket.provider}:${ticket.id}`;

          // Find the account whose member matches this ticket's provider.
          const matchingAccount = currentAccounts.find(account =>
            account.member.startsWith(ticket.provider)
          );

          if (!matchingAccount) {
            continue;
          }

          if (risk?.key && ticket.id) {
            const freshData = await fetchTicket(
              axios,
              risk.key,
              ticket.id,
              matchingAccount.key
            );

            const updatedRisk = freshData?.risks?.[0];
            const updatedTicket = updatedRisk?.tickets?.find(
              t => t.id === ticket.id
            );

            newMetadata[ticketKey] = updatedTicket
              ? {
                  status: updatedTicket.status ?? '',
                  assignee: updatedTicket.assignee ?? '',
                  resolution: updatedTicket.resolution ?? '',
                }
              : {
                  status: ticket.status,
                  assignee: ticket.assignee,
                  resolution: ticket.resolution,
                };
          }
        } catch (error) {
          console.error('Failed to fetch ticket data:', error);
        }
      }

      if (!controller.signal.aborted) {
        setRefreshedMetadata(newMetadata);
      }
    };

    fetchTicketData();

    return () => {
      controller.abort();
    };
    // Re-fetch when:
    // - vulnerability changes (risk?.key)
    // - risk status is updated post-mutation (risk?.status)
    // - embedded ticket data changes after async Jira sync (serverTicketsFingerprint)
    // serverTickets/ticketingAccounts accessed via refs to avoid unstable array deps.
  }, [risk?.key, risk?.status, serverTicketsFingerprint, axios]);

  const historyString = useMemo(
    () => JSON.stringify(risk?.history),
    [risk?.history]
  );

  const history = useMemo(() => {
    if (!risk) return [];

    const riskHistory = risk.history || [];

    const originalStatus =
      riskHistory.length > 0 ? riskHistory[0].from : risk.status;

    const firstTrackedHistory = {
      from: '',
      to: originalStatus,
      updated: risk.created,
      comment: risk.comment,
    };

    return [firstTrackedHistory, ...riskHistory];
  }, [historyString, risk?.status, risk?.created, risk?.comment]);

  const userDefinedTags = useMemo(() => {
    return risk?.tags || [];
  }, [risk?.tags]);

  const { ssoTags, isSSO } = useMemo(() => {
    const sso = (risk?.name || '').startsWith('web-login-sso');
    return {
      ssoTags: sso ? ['web-login-sso'] : [],
      isSSO: sso,
    };
  }, [risk?.name]);

  const { moreActions: baseMoreActions } = useRiskActions({
    impactedAssets,
    risks: risk ? [risk] : [],
    onSuccess: () => refetch(),
    onComment: () => setIsNoteModalOpen(true),
    showTicketActions: true,
    onCreateTicket: () => {
      setIsTicketModalOpen(true);
    },
  });

  // ✅ All hooks called - now safe to do early return

  // Show loading state while fetching data
  if (isLoading) {
    return (
      <div className="flex size-full items-center justify-center p-6">
        <Loader isLoading type="spinner" />
      </div>
    );
  }

  if (!risk) return null;

  // Handler functions defined after hooks but before render
  const handleNote = async (note: string): Promise<void> => {
    await updateRisk({
      key: risk.key,
      name: risk.name,
      status: risk.status,
      comment: note,
      showSnackbar: false,
    });
    toast.success('Note added to risk');
  };

  const handleRemoveTag = async (tagToRemove: string) => {
    try {
      const updatedTags = (risk?.tags || []).filter(tag => tag !== tagToRemove);
      await axios.put('/risk', {
        key: risk.key,
        tags: updatedTags,
      });
      queryClient.invalidateQueries({
        predicate: query => {
          const queryKey = query.queryKey as string[];
          return (
            (queryKey[0] === 'MY' && queryKey[1] === 'risk') ||
            queryKey[0] === 'GENERIC_MY_SEARCH'
          );
        },
        refetchType: 'active',
      });
      toast.success('Tag removed');
    } catch {
      toast.error('Failed to remove tag');
    }
  };

  // Filter baseMoreActions to only include ticket-related actions
  const ticketActions = baseMoreActions.filter(action =>
    ['new-ticket', 'associate-ticket', 'setup-ticketing'].includes(
      action.value as string
    )
  );

  // PlexTrac actions dropdown - only for Praetorian users
  const plexTracActions = isPraetorianUser
    ? [
        {
          label: 'Send to PlexTrac',
          icon: <ArrowTopRightOnSquareIcon className="size-5" />,
          onClick: () => setIsPlexTracModalOpen(true),
        },
        {
          label: 'Sync With PlexTrac',
          icon: <ArrowPathIcon className="size-5" />,
          onClick: () =>
            syncWithPlextrac(
              { id: risk.name, create: false },
              {
                onSuccess: () => {
                  refetchDefinitionsFile();
                  queryClient.invalidateQueries({
                    queryKey: getQueryKey.getMy('vulnerability'),
                  });
                },
                onError: () => {
                  refetchDefinitionsFile();
                  queryClient.invalidateQueries({
                    queryKey: getQueryKey.getMy('vulnerability'),
                  });
                },
              }
            ),
        },
      ]
    : [];

  // Ticketing actions - only ticket-related items
  const moreActions = ticketActions;

  return (
    <div className="flex w-full flex-col gap-6 p-6">
      <VulnerabilityDrawerHeader
        risk={risk}
        existingWriteup={existingWriteup}
        vulnerabilityTitle={vulnerabilityTitle}
        mlPrediction={mlPrediction}
        isSSO={isSSO}
        ssoTags={ssoTags}
      />
      <SimpleTags
        tags={userDefinedTags}
        onRemove={canWriteAssets ? handleRemoveTag : undefined}
        variant="compact"
      />

      <Entitled entitlement="write_assets">
        <div className="flex flex-wrap items-center gap-2">
          <VulnerabilityActions
            vulnerabilities={[risk]}
            onNoteClick={() => setIsNoteModalOpen(true)}
            onTagClick={() => setIsTagModalOpen(true)}
            onRemoveTagClick={() => setIsRemoveTagModalOpen(true)}
            onImportClick={() => setIsImportModalOpen(true)}
            onExportClick={() => setIsExportModalOpen(true)}
            onAuxInfoClick={() => setIsEditingAuxiliaryInfo(true)}
            onRefreshVisited={(comment: string) =>
              refreshVisited({ risk, comment })
            }
            isRefreshingVisited={refreshVisitedStatus === 'pending'}
            plexTracActions={plexTracActions}
            moreActions={moreActions}
            onUpdateRisk={async updatedRisk => {
              // Check if status is changing to "Opened" - this triggers Jira ticket creation
              const isChangingToOpened =
                updatedRisk.status.startsWith(RiskStatus.Opened) &&
                !risk.status.startsWith(RiskStatus.Opened);

              // Show optimistic placeholder immediately for instant feedback
              // Only show if: 1) changing to opened, 2) no optimistic placeholder, 3) no existing tickets
              if (
                isChangingToOpened &&
                !hasOptimisticTicket &&
                serverTickets.length === 0
              ) {
                addOptimisticTicket();
              }

              await handleUpdateRisk(updatedRisk);
            }}
            severityOptions={severityFilters}
            getRiskStatusLabel={getRiskStatusLabel}
            hideLabel
          />
        </div>
      </Entitled>
      <div className="bg-chariot-stroke h-px w-full"></div>
      <TabsKeyboard
        id="vulnerabilityDetailsTabs"
        tabs={tabOptions}
        keyboardDependencies={[risk.name]}
        selected={selectedTab}
        onSelect={tabOption => {
          keyboardListeners.setPOEEnabled(true);
          setCurrentTab(tabOption.value);
        }}
      />

      {/* Unified tab content structure with conditional overflow for Evidence tab */}
      <div className="w-full">
        <Container className="p-6" tabIndex={-1}>
          {selectedTab.value === 'overview' && (
            <OverviewTab
              risk={risk}
              vulnerabilityId={vulnerabilityId}
              tickets={tickets}
              refetchTickets={async () => {
                // Tickets are embedded on Risk, so refetching means invalidating risk queries
                await queryClient.invalidateQueries({
                  queryKey: getQueryKey.getMy('risk'),
                });
              }}
              removeOptimisticTicket={removeOptimisticTicket}
              restoreOptimisticTicket={restoreOptimisticTicket}
              refreshedMetadata={refreshedMetadata}
              instanceOverviewValue={instanceOverviewValue}
              definitionsFileValue={definitionsFileValue}
              isDefinitionsFileFetching={isDefinitionsFileFetching}
              _refetchDefinitionsFile={refetchDefinitionsFile}
              hasExistingWriteup={hasExistingWriteup}
              _syncWithPlextrac={syncWithPlextrac}
            />
          )}
          {selectedTab.value === 'assets' && risk?.key && (
            <AssetsTab riskKey={risk.key} />
          )}
          {selectedTab.value === 'evidence' && (
            <div data-testid="evidence-tab">
              <POESelector
                evidenceFiles={evidenceFiles || []}
                key={risk.key}
                poeLocation={`${risk.dns}/${risk.name}`}
                name={risk.name}
                source={risk.source}
                riskKey={risk.key}
                severity={risk.status[1] as RiskSeverity}
                attributes={attributesGenericSearch?.attributes}
                definitionsFileValue={definitionsFileValue}
              />
              <CorrelationGraph risk={risk} />
            </div>
          )}
          {selectedTab.value === 'history' && (
            <Timeline
              items={[
                ...(history
                  ?.map((item, itemIndex) => {
                    const { title, updated } = getHistoryDiff(
                      item,
                      itemIndex === 0,
                      accesss
                    );
                    return {
                      title,
                      description: updated,
                      className: 'text-default',
                      icon:
                        itemIndex === 0 ? (
                          <RisksIcon className="stroke-1" />
                        ) : undefined,
                    };
                  })
                  .reverse() || []),
              ]}
            />
          )}
          {selectedTab.value === 'notes' && (
            <NotesTab
              resource={risk}
              resourceType="Vulnerability"
              onOpenCommentModal={
                canWriteAssets ? () => setIsNoteModalOpen(true) : undefined
              }
            />
          )}
        </Container>
      </div>
      <NoteModal
        open={isNoteModalOpen}
        onClose={() => setIsNoteModalOpen(false)}
        onSubmit={handleNote}
        resourceType="Risk"
        mode="markdown"
        presentation="modal"
        selectedCount={1}
      />

      <AddTag
        open={isTagModalOpen}
        onClose={() => setIsTagModalOpen(false)}
        description="Tags help organize and classify vulnerabilities in your attack surface"
        disabled={false}
        entity="risk"
        onSubmit={async (newTag: string) => {
          try {
            const existingTags = risk?.tags || [];
            await axios.put('/risk', {
              key: risk.key,
              tags: [...existingTags, newTag],
            });
            queryClient.invalidateQueries({
              predicate: query => {
                const queryKey = query.queryKey as string[];
                return (
                  (queryKey[0] === 'MY' && queryKey[1] === 'risk') ||
                  queryKey[0] === 'GENERIC_MY_SEARCH'
                );
              },
              refetchType: 'active',
            });
            toast.success('Tag added');
          } catch {
            toast.error('Failed to add tag');
          }
        }}
      />

      {/* Single-item context (riskKeys=[risk.key]) — shouldUseBulk is always false; no bulk props needed. */}
      <VulnerabilityRemoveTag
        open={isRemoveTagModalOpen}
        onClose={() => setIsRemoveTagModalOpen(false)}
        riskKeys={[risk.key]}
        onSuccess={() => {
          refetch();
          setIsRemoveTagModalOpen(false);
        }}
      />

      {isTicketModalOpen && (
        <CreateTicketModal
          risk={risk}
          onClose={() => setIsTicketModalOpen(false)}
        />
      )}

      <PushToPlexTracModal
        isOpen={isPlexTracModalOpen}
        onClose={() => setIsPlexTracModalOpen(false)}
        riskKey={risk.key}
        riskName={risk.name}
      />

      <Modal
        size="6xl"
        open={isEditingAuxiliaryInfo}
        onClose={() => setIsEditingAuxiliaryInfo(false)}
        title="Auxiliary Information"
        footer={{
          text: 'Save',
          isLoading: updateFileStatus === 'pending',
          onClick: async () => {
            await updateFile({
              ignoreSnackbar: true,
              name: `evidence/${risk.key}`,
              content: auxiliaryInfoValue,
            });
            queryClient.invalidateQueries();
            setIsEditingAuxiliaryInfo(false);
          },
        }}
      >
        <div className="h-[60vh]">
          <MarkdownEditor
            value={auxiliaryInfoValue}
            onChange={value => setAuxiliaryInfoValue(value || '')}
            filePathPrefix="definitions/files"
          />
        </div>
      </Modal>

      {/* Import Modal - lazy loaded to avoid heavy dependencies */}
      {isImportModalOpen && (
        <Suspense fallback={null}>
          <ImportVulnerabilitiesModal
            open={isImportModalOpen}
            onClose={() => setIsImportModalOpen(false)}
          />
        </Suspense>
      )}

      {/* Export Modal */}
      <UniversalExportModal
        config={exportConfig}
        open={isExportModalOpen}
        onClose={() => setIsExportModalOpen(false)}
        selectedRows={[risk.key]}
        onClearSelection={() => setIsExportModalOpen(false)}
      />
    </div>
  );
};

const overviewTabs = [
  { label: 'Description', value: '' },
  { label: 'Risk Score', value: 'risk-score' },
  { label: 'Exploitation Activities', value: 'exploitation-activities' },
  { label: 'MITRE', value: 'mitre' },
];

const OverviewTab = ({
  risk,
  vulnerabilityId,
  tickets,
  refetchTickets,
  removeOptimisticTicket,
  restoreOptimisticTicket,
  refreshedMetadata,
  instanceOverviewValue,
  definitionsFileValue,
  isDefinitionsFileFetching,
  _refetchDefinitionsFile,
  hasExistingWriteup,
  _syncWithPlextrac,
}: {
  risk: Risk;
  vulnerabilityId?: string;
  tickets: EmbeddedTicket[];
  refetchTickets: () => Promise<void>;
  removeOptimisticTicket: (ticketKey: string) => void;
  restoreOptimisticTicket: () => void;
  refreshedMetadata: Record<
    string,
    { status?: string; assignee?: string; resolution?: string }
  >;
  instanceOverviewValue: string;
  definitionsFileValue: string;
  isDefinitionsFileFetching: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  _refetchDefinitionsFile: () => Promise<any>;
  hasExistingWriteup: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  _syncWithPlextrac: (definition: PlextracDefinition, options?: any) => void;
}) => {
  // Check for CVE in risk.name OR attached vulnerability's id
  const cveId = risk.name.startsWith('CVE-')
    ? risk.name
    : vulnerabilityId?.startsWith('CVE-')
      ? vulnerabilityId
      : undefined;
  const isCveRisk = Boolean(cveId);
  const { isPraetorianUser } = useAuth();
  const {
    data: globalFile,
    isFetching: isGlobalFileFetching,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    refetch: _refetchGlobalFile,
  } = useGetFile(
    {
      global: true,
      name: `definitions/${risk?.name}`,
    },
    {
      enabled: !!risk,
      retry: 0,
      staleTime: Infinity,
    }
  );

  const relatedTicketsRef = useRef<HTMLDivElement>(null);
  const markdownPreviewRef = useRef<HTMLDivElement>(null);

  const [searchParams, setSearchParams] = useSearchParams();

  const selectedTabValue = searchParams.get('risk-drawer-overview-tab');

  const { data: threatIntel, status: cveFileStatus } =
    useGetFile<EnrichedVulnerability>(
      { name: `enrichments/cve/${cveId}.json`, global: true },
      { enabled: isCveRisk }
    );

  // Check if instance, account, and global definitions have actual content (not empty)
  const hasValidInstanceContent =
    instanceOverviewValue && instanceOverviewValue.trim().length > 0;

  const hasValidAccountContent =
    definitionsFileValue && definitionsFileValue.trim().length > 0;

  const globalFileContent =
    typeof globalFile === 'string' ? globalFile : JSON.stringify(globalFile);
  const hasValidGlobalContent =
    globalFileContent && globalFileContent.trim().length > 0;

  const selectedTab =
    (selectedTabValue
      ? overviewTabs.find(tab => tab.value === selectedTabValue)
      : undefined) || overviewTabs[0];

  const { status: reportRiskStatus } = useReportRisk();

  useEffect(() => {
    function calculatePreviewWidth() {
      if (!markdownPreviewRef.current) return;

      const relatedTicketHeight = relatedTicketsRef?.current?.clientHeight || 0;

      const markdownContainer =
        markdownPreviewRef.current.querySelector('.wmde-markdown');
      if (!markdownContainer) return;

      const containerTop = markdownContainer.getBoundingClientRect().top;

      // Reset all children to original width
      const children = Array.from(markdownContainer.children) as HTMLElement[];
      children.forEach(child => {
        const childTop = child.getBoundingClientRect().top - containerTop;

        if (childTop <= relatedTicketHeight) {
          child.style.width = `calc(100% - ${relatedTicketsWidth}px)`;
        } else {
          child.style.width = '100%';
        }
      });
    }

    const relatedTicketHeightObserver = new ResizeObserver(
      calculatePreviewWidth
    );
    const markdownContainerObserver = new ResizeObserver(calculatePreviewWidth);

    if (relatedTicketsRef.current) {
      relatedTicketHeightObserver.observe(relatedTicketsRef.current);
    }

    if (markdownPreviewRef.current) {
      markdownContainerObserver.observe(markdownPreviewRef.current);
    }

    // Initial dimensions
    calculatePreviewWidth();

    return () => {
      relatedTicketHeightObserver.disconnect();
      markdownContainerObserver.disconnect();
    };
  }, [definitionsFileValue, globalFileContent, tickets.length]);

  const isOverview = selectedTab.value === '' || !threatIntel;

  const renderOverviewContent = useCallback(() => {
    // Priority: Instance > Account > Global > None
    let source = hasValidInstanceContent
      ? instanceOverviewValue
      : hasValidAccountContent
        ? definitionsFileValue
        : hasValidGlobalContent
          ? globalFileContent
          : null;

    // Strip metadata sections that should not be rendered in the UI.
    // PlexTrac path: GitHub markdown → PlexTrac parses and strips Title/Risk ID → stores as Writeup.title → Chariot syncs → S3 stores only 4 content sections.
    // AI path: AI generates markdown with Title/Risk ID → writes directly to S3 → UI renders raw markdown including metadata sections.
    // The backend extraction regex (risk.go) only recognizes 4 sections: Vulnerability Description, Impact, Recommendation, References.
    // Title comes from Risk.Name (Neo4j) and Risk ID is not displayed separately.
    // Without this stripping, these sections appear as raw markdown in the card content.
    if (source) {
      source = source
        .replace(/^####\s*Risk\s*ID\s*\n+[^\n]*\s*\n+/im, '')
        .replace(/^####\s*Title\s*\n+[^\n]*\s*\n+/im, '');
    }

    if (source) {
      return (
        <MarkdownPreview
          source={source}
          className={cn(tickets.length === 0 && `*:w-full`, 'break-words')}
          style={{
            padding: '0.75rem',
            minHeight: '20px',
          }}
          processVulnerabilityContent={true}
        />
      );
    } else {
      return (
        <div className="text-chariot-text-secondary flex h-full flex-col items-center justify-center py-4 text-center">
          <NoInformationIcon className="text-chariot-stroke mb-4 min-h-[200px]" />
          <h2 className="text-chariot-text-primary text-lg font-bold">
            No Information Collected
          </h2>
        </div>
      );
    }
  }, [
    hasValidInstanceContent,
    instanceOverviewValue,
    hasValidAccountContent,
    definitionsFileValue,
    hasValidGlobalContent,
    globalFileContent,
    tickets.length,
  ]);

  const renderEmptyPlexTracDescription = useCallback(() => {
    if (hasExistingWriteup && !globalFileContent && isPraetorianUser) {
      return (
        <div className="border-chariot-stroke-medium bg-chariot-status-medium/10 mx-3 mb-3 flex items-start gap-3 rounded-md border p-4">
          <ExclamationTriangleIcon className="text-chariot-status-medium mt-0.5 size-5 shrink-0" />
          <div className="flex flex-col gap-1">
            <p className="text-chariot-text-primary text-sm font-medium">
              Empty PlexTrac Description
            </p>
            <p className="text-chariot-text-secondary text-sm">
              Please add appropriate content to the definition file immediately.
            </p>
          </div>
        </div>
      );
    }

    return null;
  }, [hasExistingWriteup, globalFileContent, isPraetorianUser]);

  return (
    <Loader
      isLoading={
        (isCveRisk && cveFileStatus === 'pending') ||
        isDefinitionsFileFetching ||
        isGlobalFileFetching ||
        reportRiskStatus === 'pending'
      }
    >
      <div data-testid="overview-tab">
        {isCveRisk && threatIntel && (
          <Tabs
            className="mb-6"
            tabs={overviewTabs}
            selected={selectedTab}
            variant="tertiary"
            onSelect={tab => {
              setSearchParams(prev => {
                if (tab.value) {
                  prev.set('risk-drawer-overview-tab', tab.value);
                } else {
                  prev.delete('risk-drawer-overview-tab');
                }
                return prev;
              });
            }}
          />
        )}
        <div className="flex flex-col gap-6">
          {isOverview && (
            <div className="relative">
              <RelatedTickets
                risk={risk}
                isDefinitionsFile={Boolean(definitionsFileValue)}
                tickets={tickets}
                refetchTickets={refetchTickets}
                removeOptimisticTicket={removeOptimisticTicket}
                restoreOptimisticTicket={restoreOptimisticTicket}
                refreshedMetadata={refreshedMetadata}
                ref={relatedTicketsRef}
                className="float-right ml-6"
              />
              <div className="w-full">
                <div ref={markdownPreviewRef}>
                  {renderEmptyPlexTracDescription()}
                  {renderOverviewContent()}
                </div>
              </div>
            </div>
          )}
          {!isOverview && (
            <>
              {selectedTab.value === 'risk-score' && (
                <>
                  <CVSSComponent threatIntel={threatIntel} />
                  <EPSSComponent threatIntel={threatIntel} />
                </>
              )}
              {selectedTab.value === 'exploitation-activities' && (
                <>
                  <ExploitsTimelineComponent threatIntel={threatIntel} />
                  <ThreatActorsComponent threatIntel={threatIntel} />
                </>
              )}
              {selectedTab.value === 'mitre' && (
                <>
                  <MitreAttacksComponent threatIntel={threatIntel} />
                  <MitreWeaknessesComponent threatIntel={threatIntel} />
                </>
              )}
            </>
          )}
        </div>
      </div>
    </Loader>
  );
};

const RelatedTickets = forwardRef<
  HTMLDivElement,
  {
    risk: Risk;
    isDefinitionsFile: boolean;
    tickets: EmbeddedTicket[];
    refetchTickets: () => Promise<void>;
    removeOptimisticTicket: (ticketKey: string) => void;
    restoreOptimisticTicket: () => void;
    refreshedMetadata: Record<
      string,
      { status?: string; assignee?: string; resolution?: string }
    >;
    className?: string;
  }
>(function RelatedTickets(
  {
    risk,
    tickets,
    refetchTickets,
    removeOptimisticTicket,
    restoreOptimisticTicket,
    refreshedMetadata,
    className,
  },
  ref
) {
  const { currentTheme } = useTheme();
  const { mutateAsync: disconnectTicket } = useDisconnectTicket();

  // Derive ticketing config from AllIntegrations using provider name
  const getTicketIcon = (provider: string) => {
    const integration = Object.values(AllIntegrations).find(
      i => i.ticketing && i.ticketAttributeName === provider
    );
    return {
      icon: integration?.logo ?? 'jira.svg',
      label: integration?.name ?? 'Ticket',
    };
  };

  // Only show expanded state when there are multiple tickets
  const [expandedTicketId, setExpandedTicketId] = useState<string | null>(null);
  const hasMultipleTickets = tickets.length > 1;

  const toggleTicket = (ticketId: string) => {
    if (hasMultipleTickets) {
      setExpandedTicketId(prev => (prev === ticketId ? null : ticketId));
    }
  };

  useEffect(() => {
    const interval = setInterval(() => {
      refetchTickets();
    }, mToMs(1));

    return () => clearInterval(interval);
  }, [refetchTickets]);

  if (tickets.length === 0) {
    return null;
  }

  return (
    <div className={cn('h-fit shrink-0', 'w-[284px]', className)} ref={ref}>
      <div className="border-chariot-stroke bg-chariot-background-primary flex w-full flex-col border-l pl-6">
        <h1 className="pb-2 font-bold">Related Tickets</h1>
        <div className="flex flex-col overflow-hidden rounded-lg">
          {tickets?.map((ticket, index) => {
            const ticketKey = getTicketKey(ticket);
            const isExpanded =
              !hasMultipleTickets || expandedTicketId === ticket.id;
            const isLast = tickets.length - 1 === index;
            const isOptimistic = Boolean(ticket.isOptimistic);
            // Prefer refreshed metadata; fall back to embedded ticket fields
            const displayStatus =
              refreshedMetadata[ticketKey] !== undefined
                ? refreshedMetadata[ticketKey].status
                : ticket.status;
            const displayAssignee =
              refreshedMetadata[ticketKey]?.assignee ?? ticket.assignee;
            const displayResolution =
              refreshedMetadata[ticketKey]?.resolution ?? ticket.resolution;
            const displayLink = ticket.link;

            return (
              <div
                key={ticketKey}
                className={cn(
                  'border-chariot-stroke',
                  !isLast && 'border-b',
                  isExpanded &&
                    hasMultipleTickets &&
                    'bg-chariot-background-secondary',
                  // Add subtle pulse animation for optimistic placeholder
                  isOptimistic && 'animate-pulse opacity-70'
                )}
                data-testid={
                  isOptimistic ? 'optimistic-ticket' : 'related-ticket'
                }
              >
                <div
                  onClick={() => !isOptimistic && toggleTicket(ticket.id)}
                  className={cn(
                    'flex w-full items-center justify-between p-3 text-left',
                    isOptimistic ? 'cursor-default' : 'cursor-pointer'
                  )}
                  role="button"
                >
                  <div className="flex items-center gap-2">
                    {hasMultipleTickets && !isOptimistic && (
                      <ChevronDownIcon
                        className={`mr-1 size-4 shrink-0 stroke-[4px] transition-transform${
                          isExpanded ? 'rotate-180' : ''
                        }`}
                      />
                    )}
                    <img
                      src={`/icons/${currentTheme}/${getTicketIcon(ticket.provider).icon}`}
                      alt={`${getTicketIcon(ticket.provider).label} icon`}
                      className={cn('size-5', isOptimistic && 'opacity-50')}
                    />
                    <p className="text-sm font-bold text-nowrap">
                      {isOptimistic ? 'Creating Ticket' : 'Ticket ID'}
                    </p>
                    {isOptimistic ? (
                      // Show simple "Creating..." text for optimistic placeholder
                      <span className="text-chariot-text-secondary animate-pulse text-sm italic">
                        {ticket.id}
                      </span>
                    ) : displayLink ? (
                      // Direct link to ticket when link is available
                      <a
                        href={displayLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-chariot-brand-primary flex items-center gap-1 text-sm font-bold hover:underline"
                        aria-label={`Open ticket ${ticket.id} in new tab`}
                        data-testid="related-ticket-link"
                        onClick={e => e.stopPropagation()}
                      >
                        {ticket.id}
                        <ArrowTopRightOnSquareIcon className="size-3" />
                      </a>
                    ) : (
                      // Plain text when no link is available
                      <span className="text-sm font-bold">{ticket.id}</span>
                    )}
                  </div>
                  {/* Actions menu - always visible for non-optimistic tickets */}
                  {!isOptimistic && (
                    <JiraTicketActionsMenu
                      ticket={ticket}
                      onDisconnect={async () => {
                        // Optimistic removal: hide ticket immediately
                        removeOptimisticTicket(ticketKey);

                        try {
                          // Mutation with toast (toast already in hook)
                          await disconnectTicket({
                            riskKey: risk.key,
                            provider: ticket.provider,
                            ticketId: ticket.id,
                          });

                          // Invalidate risk queries so tickets list refreshes
                          queryClient.invalidateQueries({
                            queryKey: getQueryKey.genericSearch(''),
                          });

                          // Refetch tickets to get updated list
                          await refetchTickets();
                        } catch {
                          // Rollback optimistic removal on error
                          restoreOptimisticTicket();
                        }
                      }}
                    />
                  )}
                </div>
                {isExpanded && !isOptimistic && (
                  <div className="flex flex-col gap-2 p-3 pt-0">
                    <div className="flex items-center gap-2">
                      <h3 className="text-sm font-bold">Status</h3>
                      <DashedTag label={displayStatus || '-'} size="xs" />
                    </div>
                    {displayResolution && (
                      <div className="flex items-center gap-2">
                        <h3 className="text-sm font-bold">Resolution</h3>
                        <DashedTag label={displayResolution} size="xs" />
                      </div>
                    )}
                    {displayAssignee && (
                      <div className="flex items-center gap-2">
                        <h3 className="text-sm font-bold">Assignee</h3>
                        <span className="text-sm">{displayAssignee}</span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
});

export function TicketModals() {
  const { mutateAsync: associateTicket } = useAssociateTicket();
  const jiraConfig = AllIntegrations['jira'];
  const { currentTheme } = useTheme();
  const {
    modal: { ticket },
  } = useGlobalState();
  const { data: accounts, status: accountStatus } = useUserSettings(
    undefined,
    'jira'
  );

  const [associatedTicketId, setAssociatedTicketId] = useState<string | null>(
    null
  );
  const [selectedAccountOption, setSelectedAccountOption] = useState<
    DropdownOption | undefined
  >();

  const JiraIcon = (
    <img
      src={`/icons/${currentTheme}/${jiraConfig?.logo}`}
      alt={jiraConfig.name}
      className="size-6"
    />
  );

  const accountsString = useMemo(() => JSON.stringify(accounts), [accounts]);

  // Use setTimeout to defer setState, avoiding synchronous cascading renders
  useEffect(() => {
    if (accounts?.length) {
      const timer = setTimeout(() => {
        setSelectedAccountOption({
          label: accounts[0].settings?.displayName ?? '',
          value: accounts[0].key,
        });
      }, 0);
      return () => clearTimeout(timer);
    }
  }, [accountsString]);

  // Use setTimeout to defer setState, avoiding synchronous cascading renders
  useEffect(() => {
    if (ticket.associateTicket.open) {
      const timer = setTimeout(() => setAssociatedTicketId(null), 0);

      return () => {
        clearTimeout(timer);
        setAssociatedTicketId(null);
      };
    }
  }, [ticket.associateTicket.open]);

  return (
    <>
      <Modal
        baseClassName="overflow-visible"
        open={ticket.associateTicket.open}
        onClose={() => ticket.associateTicket.onOpenChange(false)}
        title={
          <div className="flex w-full flex-row items-center justify-between">
            <p>Associate Existing Ticket</p>
            <Dropdown
              listClassName="right-0 z-200"
              triggerClassName="max-w-[200px] [&>span:nth-child(2)]:text-ellipsis [&>span:nth-child(2)]:overflow-hidden"
              options={(accounts ?? [])?.map(account => ({
                label: account.settings?.displayName ?? '',
                value: account.key,
                // TODO: In future when we have multiple integrations, update this to be dynamic
                icon: JiraIcon,
              }))}
              icon={JiraIcon}
              selected={selectedAccountOption ? [selectedAccountOption] : []}
              onSelect={option => {
                setSelectedAccountOption(option);
              }}
            />
          </div>
        }
        footer={{
          text: 'Save',
          form: 'associate-ticket-form',
          disabled: accountStatus === 'pending',
        }}
      >
        <form
          id="associate-ticket-form"
          onSubmit={async e => {
            e.preventDefault();
            const promises = ticket.associateTicket?.riskKeys?.map(
              async riskKey => {
                await associateTicket({
                  account: String(selectedAccountOption?.value || ''),
                  riskKey,
                  ticketId: associatedTicketId || '',
                });
              }
            );
            await Promise.all(promises);

            // Invalidate queries (mark as stale)
            queryClient.invalidateQueries({
              queryKey: getQueryKey.genericSearch(''),
            });
            queryClient.invalidateQueries({
              queryKey: getQueryKey.getMy('risk'),
            });

            // Immediately refetch active queries to update drawer
            await Promise.all([
              queryClient.refetchQueries({
                queryKey: getQueryKey.genericSearch(''),
                type: 'active',
              }),
              queryClient.refetchQueries({
                queryKey: getQueryKey.getMy('risk'),
                type: 'active',
              }),
            ]);

            ticket.associateTicket.onOpenChange(false);
          }}
        >
          <label className="flex flex-col gap-1">
            Ticket ID
            <InputText
              name="associatedTicketId"
              value={associatedTicketId || ''}
              onChange={e => setAssociatedTicketId(e.target.value)}
              placeholder="Enter existing ticket ID"
              required
            />
          </label>
        </form>
      </Modal>
      {ticket.setup.open && (
        <SetupModal
          isOpen={ticket.setup.open}
          onClose={() => ticket.setup.onOpenChange(false)}
          onComplete={() => ticket.setup.onOpenChange(false)}
          selectedIntegration={undefined}
          formData={{}}
          integration={AllIntegrations.jira}
        />
      )}
    </>
  );
}

export const CreateTicketModal = ({
  risk,
  onClose,
}: {
  risk: Risk;
  onClose: () => void;
}) => {
  const { currentTheme } = useTheme();

  // Fetch accounts for all ticketing integrations
  const { accounts: integrationAccounts, isLoading: isLoadingAccounts } =
    useTicketingAccounts();

  const { mutateAsync: createTicket, status: createTicketStatus } =
    useCreateTicket();

  const [selectedAccountOption, setSelectedAccountOption] = useState<
    DropdownOption | undefined
  >();
  const [step, setStep] = useState<
    'listTemplates' | 'configuration' | 'permissions'
  >('listTemplates');
  const [selectedTemplate, setSelectedTemplate] = useState<JiraTemplate>();
  const selectedAccount: Account | undefined = integrationAccounts?.find(
    account => account.value === selectedAccountOption?.value
  );
  const disabledAction =
    !selectedAccountOption ||
    isLoadingAccounts ||
    createTicketStatus === 'pending';

  const integrationIcon = (member: string) => {
    const config = getTicketingIntegration(member);
    return (
      <img
        src={`/icons/${currentTheme}/${config?.logo}`}
        alt={config?.name ?? 'Ticketing'}
        className="size-6"
      />
    );
  };

  const SelectedIcon = selectedAccount
    ? integrationIcon(selectedAccount.member)
    : integrationIcon('jira');

  const integrationAccountsString = useMemo(
    () => JSON.stringify(integrationAccounts),
    [integrationAccounts]
  );

  // Use setTimeout to defer setState, avoiding synchronous cascading renders
  useEffect(() => {
    if (integrationAccounts?.length) {
      const timer = setTimeout(() => {
        setSelectedAccountOption({
          label: integrationAccounts[0].settings?.displayName ?? '',
          value: integrationAccounts[0].value,
        });
      }, 0);
      return () => clearTimeout(timer);
    }
  }, [integrationAccountsString]);

  async function handleCreateTicket() {
    const risks = [risk];
    const promises = risks.map(risk => {
      return createTicket({
        riskKey: risk.key,
        account: selectedAccount?.key ?? '',
        templateId: selectedTemplate?.id ?? '',
      });
    });
    await Promise.all(promises);

    // Invalidate queries (mark as stale)
    queryClient.invalidateQueries({
      queryKey: getQueryKey.genericSearch(''),
    });
    queryClient.invalidateQueries({
      queryKey: getQueryKey.getMy('risk'),
    });

    // Immediately refetch active queries to update drawer
    await Promise.all([
      queryClient.refetchQueries({
        queryKey: getQueryKey.genericSearch(''),
        type: 'active',
      }),
      queryClient.refetchQueries({
        queryKey: getQueryKey.getMy('risk'),
        type: 'active',
      }),
    ]);

    onClose();
  }

  return (
    <>
      {step === 'listTemplates' && (
        <Modal
          open={true}
          onClose={onClose}
          size="4xl"
          title={
            <div className="flex w-full flex-row items-center justify-between">
              <p>Select Integration and Project for Ticket Routing</p>
              <Dropdown
                listClassName="right-0"
                options={(integrationAccounts ?? [])?.map(account => ({
                  label: account.settings?.displayName ?? '',
                  value: account.value,
                  icon: integrationIcon(account.member),
                }))}
                icon={SelectedIcon}
                selected={selectedAccountOption ? [selectedAccountOption] : []}
                onSelect={option => {
                  setSelectedAccountOption(option);
                  setSelectedTemplate(undefined);
                }}
              />
            </div>
          }
          footer={{
            className: 'w-fit px-2',
            hideCancelButton: false,
            left: (
              <Button
                className="text-chariot-brand-primary border-none bg-transparent p-0 underline hover:bg-transparent"
                variant="ghost"
                onClick={() => {
                  setStep('configuration');
                }}
                disabled={disabledAction}
              >
                <PlusIcon className="size-4" />
                Add Project
              </Button>
            ),
            text: 'Confirm',
            onClick: handleCreateTicket,
            disabled: disabledAction || !selectedTemplate,
          }}
        >
          <Container>
            <CreateTicketTable
              data={selectedAccount}
              status={isLoadingAccounts ? 'pending' : 'success'}
              value={String(selectedAccountOption?.value || '')}
              selectedTemplate={selectedTemplate}
              setSelectedTemplate={setSelectedTemplate}
              icon={SelectedIcon}
              provider={selectedAccount?.member ?? 'jira'}
            />
          </Container>
        </Modal>
      )}
      {step === 'configuration' && (
        <SetupModal
          step="configuration"
          isOpen={true}
          onClose={() => setStep('listTemplates')}
          onComplete={() => setStep('listTemplates')}
          selectedIntegration={selectedAccount}
          formData={{
            displayName: selectedAccountOption?.label || '',
          }}
        />
      )}
    </>
  );
};

export const CreateTicketTable = ({
  data,
  status: statusProps,
  value,
  selectedTemplate,
  setSelectedTemplate,
  icon,
  provider = 'jira',
}: {
  data?: Account;
  value: string;
  status: 'pending' | 'error' | 'success';
  selectedTemplate?: JiraTemplate;
  setSelectedTemplate: (project: JiraTemplate) => void;
  icon: React.ReactNode;
  provider?: string;
}) => {
  const {
    templatesError,
    error,
    status: { isValidating, disconnectProjectStatus },
    onDisconnectTemplate,
    onValidateIntegration,
  } = useIntegrationSettings(provider, value, { enabled: false });
  const status =
    [statusProps, disconnectProjectStatus].includes('pending') || isValidating
      ? 'pending'
      : 'success';

  const className = 'group-[.is-disabled]:pointer-events-none';

  const dataString = useMemo(() => JSON.stringify({ data }), [data]);

  useEffect(() => {
    if (data && data.key) {
      onValidateIntegration({
        account: data.key,
        config: data.config,
        settings: data.settings,
      });
    }
  }, [dataString]);

  return (
    <Table
      name="projects"
      key={data?.key}
      rowClassName={row => {
        if (templatesError?.[row?.id]) {
          return 'text-chariot-stroke cursor-not-allowed group is-disabled';
        }
        return '';
      }}
      columns={[
        {
          id: '',
          label: '',
          fixedWidth: 30,
          cell: row => {
            if (!templatesError?.[row?.id]) {
              return null;
            }

            return (
              <InsufficientPermissionsTooltip
                template={row}
                templateError={templatesError[row.id]}
                onDisconnectTemplate={onDisconnectTemplate}
              />
            );
          },
        },
        {
          id: '',
          label: '',
          className,
          fixedWidth: 50,
          cell: row => {
            const checked = row.id === selectedTemplate?.id;
            return (
              <div
                className={cn(
                  'relative size-5 shrink-0 rounded-full border-2',
                  'flex items-center justify-center',
                  checked
                    ? 'border-chariot-brand-primary'
                    : 'border-chariot-stroke'
                )}
              >
                <div
                  className={cn(
                    'bg-chariot-brand-primary size-3 rounded-full transition-all duration-200',
                    checked ? 'scale-100' : 'scale-0'
                  )}
                />
              </div>
            );
          },
        },
        {
          id: '',
          label: 'Type',
          className,
          fixedWidth: 50,
          cell: () => icon,
        },
        {
          id: 'projectKey',
          label: 'Project Key',
          className,
          fixedWidth: 150,
        },
        {
          id: 'projectName',
          label: 'Project Name',
          className,
        },
        {
          id: 'issueType',
          label: 'Issue Type',
          className,
          fixedWidth: 100,
        },
        {
          id: 'severities',
          label: 'Auto Ticket Creation',
          className,
          fixedWidth: 200,
          cell: row => {
            const options = generateSeverityOptions();
            const autoCreate = row?.autoCreate === 'true';

            const label = options.find(
              option => option.value === row.severities
            )?.label;
            return autoCreate ? label : '-';
          },
        },
      ]}
      onRowClick={row => {
        setSelectedTemplate(row);
      }}
      loadingRowCount={4}
      selection={{
        hide: true,
      }}
      data={(data?.settings?.templates ?? []).map(template => {
        return template as unknown as JiraTemplate;
      })}
      status={status}
      error={error}
    />
  );
};
